import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Typography,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add, Delete, Edit, PowerSettingsNew, ExpandMore, ExpandLess } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { useOutageWindowData, OutageWindowForm as OutageWindowFormType } from "../hooks/useOutageWindowData";
import { useGetSchedules } from "../mutations";
import { formatDate } from "../../../shared/utils";
import { canActivateOutageWindow } from "../utils/outageWindowUtils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useState } from "react";
import { useCapabilities } from "../../../hooks/useCapabilities";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface OutageWindowFormProps {
  projectId: string | undefined;
}

interface WindowFieldErrors {
  startDate?: string;
  endDate?: string;
}

interface DeleteConfirmation {
  open: boolean;
  windowId: number | null;
}

interface CommissioningPhaseValidationError {
  open: boolean;
  commissioningPhaseEndDate: string | null;
}

const OutageWindowHeader: React.FC<{ title: string; status?: string; displayStatus?: string }> = ({
  title,
  status,
  displayStatus,
}) => {
  const getStatusColor = () => {
    if (status === "active") return "success";
    if (status === "expired") return "disabled";
    return "error";
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bgcolor={status === "inactive" ? "error.lighter" : status === "active" ? "success.lighter" : status === "expired" ? "action.hover" : "action.hover"}
      py={1.5}
      px={2}
      borderRadius="4px 4px 0 0"
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        {displayStatus && (
          <Chip
            label={displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
            size="small"
            color={getStatusColor() as any}
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
      </Box>
    </Box>
  );
};

const OutageWindowFormRow: React.FC<{
  window: OutageWindowFormType;
  windowId: number | string;
  errors?: WindowFieldErrors;
  onStartDateChange: (value: any) => void;
  onEndDateChange: (value: any) => void;
  onRemove?: () => void;
  isNew?: boolean;
}> = ({
  window,
  windowId,
  errors = {},
  onStartDateChange,
  onEndDateChange,
  onRemove,
  isNew = false,
}) => {
  const today = dayjs().startOf("day");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <DatePicker
            label="Start Date"
            value={window.outageStartDate}
            onChange={onStartDateChange}
            format="DD/MM/YY"
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.startDate,
                helperText: errors.startDate,
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DatePicker
            label="End Date"
            value={window.outageEndDate}
            onChange={onEndDateChange}
            minDate={today}
            format="DD/MM/YY"
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.endDate,
                helperText: errors.endDate,
              },
            }}
          />
        </Box>
        {isNew && onRemove && (
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <IconButton
              size="small"
              color="error"
              onClick={onRemove}
              title="Remove this date pair"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const OutageWindowForm: React.FC<OutageWindowFormProps> = ({ projectId }) => {
  const {
    outageWindowsData,
    isLoading,
    newOutageWindows,
    isEditing,
    editingWindowId,
    editWindow,
    validationErrors,
    submitError,
    successMessage,
    clearMessages,
    handleAddOutageWindow,
    handleRemoveNewOutageWindow,
    handleChangeNewWindow,
    handleSubmitNewWindows,
    handleEnableEdit,
    handleDisableEdit,
    handleChangeEditWindow,
    handleSubmitEdit,
    handleDeleteOutageWindow,
    handleDeactivateWindow,
    handleActivateWindow,
  } = useOutageWindowData({
    projectId,
  });

  const { isAllowedToAddOutageWindow, isAllowedToEditOutageWindow, isAllowedToDeleteOutageWindow, isAllowedToActivateOutageWindow, isAllowedToDeactivateOutageWindow } = useCapabilities();

  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({ open: false, windowId: null });
  const [isExistingWindowsCollapsed, setIsExistingWindowsCollapsed] = useState(false);
  const [commissioningPhaseValidationError, setCommissioningPhaseValidationError] = useState<CommissioningPhaseValidationError>({
    open: false,
    commissioningPhaseEndDate: null,
  });

  // Fetch schedules to check for commissioning phase
  const { data: schedulesData } = useGetSchedules(projectId ? Number(projectId) : 0);

  // Override handleSubmitNewWindows to add commissioning phase validation
  const originalHandleSubmitNewWindows = handleSubmitNewWindows;
  const handleSubmitNewWindowsWithValidation = async () => {
    // Check commissioning phase validation for all windows
    for (const window of newOutageWindows) {
      if (!validateCommissioningPhase(window.outageEndDate)) {
        return;
      }
    }
    await originalHandleSubmitNewWindows();
  };

  // Override handleSubmitEdit to add commissioning phase validation
  const originalHandleSubmitEdit = handleSubmitEdit;
  const handleSubmitEditWithValidation = async () => {
    if (!validateCommissioningPhase(editWindow?.outageEndDate ?? null)) {
      return;
    }
    await originalHandleSubmitEdit();
  };

  // Find commissioning phase
  const commissioningPhase = schedulesData?.data?.rows?.find(
    (item) => item.type === "phase" && item.name?.toLowerCase().includes("commissioning")
  );

  // Validate outage window against commissioning phase
  const validateCommissioningPhase = (outageEndDate: dayjs.Dayjs | null): boolean => {
    if (!commissioningPhase || !commissioningPhase.planned_end_date || !outageEndDate) {
      return true;
    }

    // If commissioning phase is completed, no date restrictions apply
    if (commissioningPhase.status === "completed") {
      return true;
    }

    const commissioningEndDate = dayjs(commissioningPhase.planned_end_date);

    if (!outageEndDate.isAfter(commissioningEndDate) && !outageEndDate.isSame(commissioningEndDate, "day")) {
      setCommissioningPhaseValidationError({
        open: true,
        commissioningPhaseEndDate: formatDate(commissioningEndDate.toDate()),
      });
      return false;
    }
    return true;
  };

  const handleCommissioningPhaseValidationClose = () => {
    setCommissioningPhaseValidationError({ open: false, commissioningPhaseEndDate: null });
  };

  const handleDeleteClick = (id: number) => {
    setDeleteConfirmation({ open: true, windowId: id });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation.windowId !== null) {
      await handleDeleteOutageWindow(deleteConfirmation.windowId);
      setDeleteConfirmation({ open: false, windowId: null });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ open: false, windowId: null });
  };

  // Loading state
  if (isLoading && outageWindowsData.length === 0) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography color="textSecondary">Loading outage windows...</Typography>
      </Box>
    );
  }

  // No data state
  if (isAllowedToAddOutageWindow && !isLoading && outageWindowsData.length === 0 && newOutageWindows.length === 0) {
    return (
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Box p={3} textAlign="center" bgcolor="action.hover" borderRadius={1}>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            No outage windows found
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddOutageWindow}
            color="primary"
          >
            Create First Outage Window
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={2} display="flex" flexDirection="column" gap={3}>
      {/* Messages */}
      {successMessage && (
        <Alert severity="success" onClose={clearMessages}>
          {successMessage}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" onClose={clearMessages}>
          {submitError}
        </Alert>
      )}

      {/* Add New Button */}
      {isAllowedToAddOutageWindow && newOutageWindows.length === 0 && !isEditing && outageWindowsData.length > 0 && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="text"
            startIcon={<Add />}
            onClick={handleAddOutageWindow}
            color="primary"
          >
            ADD NEW OUTAGE WINDOW
          </Button>
        </Box>
      )}

      {/* New Outage Windows - Add Mode */}
      {newOutageWindows.length > 0 && !isEditing && (
        <Box display="flex" flexDirection="column" gap={2} mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            New Outage Windows ({newOutageWindows.length})
          </Typography>

          {newOutageWindows.map((window, index) => (
            <Card key={window.id} variant="outlined" sx={{ overflow: "hidden" }}>
              <OutageWindowHeader title={`New Window ${index + 1}`} />
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <OutageWindowFormRow
                  window={window}
                  windowId={window.id!}
                  errors={validationErrors[window.id!]}
                  onStartDateChange={(date) => handleChangeNewWindow(window.id!, "startDate", date)}
                  onEndDateChange={(date) => handleChangeNewWindow(window.id!, "endDate", date)}
                  onRemove={() => handleRemoveNewOutageWindow(window.id!)}
                  isNew
                />
              </Box>
            </Card>
          ))}

          <Divider />

          <Box display="flex" gap={2} justifyContent="space-between" mb={2}>
            <Button
              variant="text"
              startIcon={<Add />}
              onClick={handleAddOutageWindow}
              sx={{ color: "primary.main" }}
            >
              Add Another Date Pair
            </Button>
            <Stack direction="row" gap={1}>
              <Button variant="outlined" onClick={() => newOutageWindows.forEach(w => handleRemoveNewOutageWindow(w.id!))}>
                Clear All
              </Button>
              <Button variant="contained" color="primary" onClick={handleSubmitNewWindowsWithValidation}>
                {newOutageWindows.length === 1 ? "Save Date" : "Save All Dates"}
              </Button>
            </Stack>
          </Box>

          <Divider />

        </Box>
      )}

      {/* Existing Outage Windows - View/Edit Mode */}
      {outageWindowsData.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              Existing Outage Windows ({outageWindowsData.length})
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsExistingWindowsCollapsed(!isExistingWindowsCollapsed)}
            >
              {isExistingWindowsCollapsed ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          </Box>

          {!isExistingWindowsCollapsed && (
            outageWindowsData.map((window, index) =>
              isEditing && editingWindowId === window.id ? (
              // Edit Mode
              <Card key={window.id} variant="outlined" sx={{ overflow: "hidden" }}>
                <OutageWindowHeader
                  title={`Editing: Outage Window ${index + 1}`}
                  status={window.status?.toLowerCase()}
                  displayStatus={window.status_display_text}
                />
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <OutageWindowFormRow
                    window={editWindow!}
                    windowId={window.id}
                    errors={validationErrors.edit}
                    onStartDateChange={(date) => handleChangeEditWindow("startDate", date)}
                    onEndDateChange={(date) => handleChangeEditWindow("endDate", date)}
                  />
                  <Divider />
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button variant="outlined" onClick={handleDisableEdit}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmitEditWithValidation}>
                      Save Changes
                    </Button>
                  </Box>
                </Box>
              </Card>
            ) : (
              // View Mode
              <Card key={window.id} variant="outlined" sx={{ overflow: "hidden" }}>
                <OutageWindowHeader
                  title={`Outage Window ${index + 1}`}
                  status={window.status?.toLowerCase()}
                  displayStatus={window.status_display_text}
                />
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Start Date
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(window.outage_start_date)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        End Date
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(window.outage_end_date)}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box display="flex" gap={1} flexWrap="wrap">
                    {isAllowedToEditOutageWindow && <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit fontSize="small" />}
                      onClick={() => handleEnableEdit(window.id)}
                    >
                      Edit
                    </Button>}
                    {(() => {
                      const status = window.status?.toLowerCase();
                      const canActivate = canActivateOutageWindow(dayjs(), window.outage_start_date, window.outage_end_date);
                      
                      // Show Deactivate if status is active
                      if (isAllowedToDeactivateOutageWindow && status === "active") {
                        return (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<PowerSettingsNew fontSize="small" />}
                            onClick={() => handleDeactivateWindow(window.id)}
                          >
                            Deactivate
                          </Button>
                        );
                      } 
                      // Show Activate if status is inactive AND we're within the window period
                      else if (isAllowedToActivateOutageWindow && status === "in_active" && canActivate) {
                        return (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<PowerSettingsNew fontSize="small" />}
                            onClick={() => handleActivateWindow(window.id)}
                          >
                            Activate
                          </Button>
                        );
                      }
                      return null;
                    })()}
                    {isAllowedToDeleteOutageWindow && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Delete fontSize="small" />}
                      onClick={() => handleDeleteClick(window.id)}
                    >
                      Delete
                    </Button>
                    )}
                  </Box>
                </Box>
              </Card>
            )
            )
          )}
        </Box>
      )}

      {/* Commissioning Phase Validation Error Dialog */}
      <Dialog open={commissioningPhaseValidationError.open} onClose={handleCommissioningPhaseValidationClose}>
        <DialogTitle>Outage Window Date Validation</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The Outage End Date must be equal to or after the Commissioning Phase Planned End Date.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Commissioning Phase Planned End Date: <strong>{commissioningPhaseValidationError.commissioningPhaseEndDate}</strong>
          </Typography>
          <Typography>
            Please change the Commissioning Phase's Planned End Date and try again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCommissioningPhaseValidationClose} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.open} onClose={handleCancelDelete}>
        <DialogTitle>Delete Outage Window</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this outage window? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutageWindowForm;
